<template>
  <div class="error-boundary">
    <a-result
      :status="errorType"
      :title="errorTitle"
      :sub-title="errorMessage"
    >
      <template #extra>
        <a-space direction="vertical" size="middle">
          <a-button v-if="showDetails" type="link" @click="toggleDetails">
            {{ showErrorDetails ? '隐藏' : '显示' }}详细信息
          </a-button>
        </a-space>
      </template>

      <!-- 错误详情 -->
      <div v-if="showErrorDetails && errorDetails" class="error-details">
        <a-alert
          :message="errorDetails.message"
          :description="errorDetails.stack"
          type="error"
          show-icon
          closable
        />
      </div>

      <!-- 解决方案建议 -->
      <div v-if="solutionTips" class="solution-tips">
        <a-alert
          :message="solutionTitle"
          :description="solutionTips"
          type="info"
          show-icon
        />
      </div>
    </a-result>
  </div>
</template>

<script>
export default {
  name: 'ErrorBoundary',
  props: {
    error: {
      type: [Error, String, Object],
      default: null
    },
    errorType: {
      type: String,
      default: '500'
    },
    errorTitle: {
      type: String,
      default: '页面加载失败'
    },
    errorMessage: {
      type: String,
      default: '抱歉，页面加载时遇到了问题。您可以尝试重新加载或返回首页。'
    },
    showDetails: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      retrying: false,
      showErrorDetails: false,
      errorDetails: null,
      solutionTitle: '解决方案建议',
      solutionTips: ''
    }
  },
  created() {
    if (this.error) {
      this.parseError(this.error)
      this.generateSolutionTips(this.error)
    }
  },
  methods: {
    parseError(error) {
      if (typeof error === 'string') {
        this.errorDetails = {
          message: error,
          stack: ''
        }
      } else if (error instanceof Error) {
        this.errorDetails = {
          message: error.message,
          stack: error.stack
        }
      } else if (error && typeof error === 'object') {
        this.errorDetails = {
          message: error.message || error.msg || JSON.stringify(error),
          stack: error.stack || ''
        }
      }
    },

    generateSolutionTips(error) {
      const errorMessage = (error.message || error.msg || '').toLowerCase();

      if (errorMessage.includes('timeout') || errorMessage.includes('超时')) {
        this.solutionTips = '1. 检查网络连接是否稳定\n2. 稍后重试加载\n3. 联系系统管理员确认服务状态';
      } else if (errorMessage.includes('network') || errorMessage.includes('网络')) {
        this.solutionTips = '1. 检查网络连接\n2. 刷新页面重试\n3. 清除浏览器缓存后重试';
      } else if (errorMessage.includes('404')) {
        this.solutionTips = '1. 确认访问的页面是否存在\n2. 联系系统管理员确认应用配置\n3. 返回首页重新导航';
      } else if (errorMessage.includes('500') || errorMessage.includes('internal server error')) {
        this.solutionTips = '1. 等待一段时间后重试\n2. 联系系统管理员报告问题\n3. 查看系统公告了解维护信息';
      } else if (errorMessage.includes('died in status loading_source_code')) {
        this.solutionTips = '1. 确认微应用服务是否正常运行\n2. 检查微应用配置是否正确\n3. 联系微应用开发团队确认部署状态';
      } else if (errorMessage.includes('mount')) {
        this.solutionTips = '1. 刷新页面重试\n2. 清除浏览器缓存\n3. 联系技术支持确认微应用兼容性';
      } else {
        this.solutionTips = '1. 刷新页面重试\n2. 清除浏览器缓存\n3. 联系技术支持获取帮助';
      }
    },

    async handleRetry() {
      this.retrying = true
      try {
        await this.$emit('retry')
        this.$message.success('重新加载成功')
      } catch (error) {
        this.$message.error('重新加载失败，请稍后再试')
        console.error('Retry failed:', error)
      } finally {
        this.retrying = false
      }
    },

    goHome() {
      this.$router.push('/home')
    },

    toggleDetails() {
      this.showErrorDetails = !this.showErrorDetails
    }
  }
}
</script>

<style lang="less" scoped>
.error-boundary {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 400px;
  padding: 20px;

  .error-details {
    margin-top: 20px;
    max-width: 600px;

    .ant-alert {
      text-align: left;
    }
  }

  .solution-tips {
    margin-top: 20px;
    max-width: 600px;

    .ant-alert {
      text-align: left;

      ::v-deep .ant-alert-description {
        white-space: pre-line;
      }
    }
  }

  .ant-result {
    padding: 24px 0;
  }
}

// 针对frame容器的特殊样式
#frame .error-boundary {
  height: 100%;
  background-color: #f5f5f5;
}
</style>